import $ from 'zepto';

import { hideItemsOnBodyClick } from '../../utils/legacy/index';
import globalInitializer from '../../initializer';
import { getHtml, safeGet } from '../../utils/ajax';
import {
  disableTabs,
  enableTabs,
  stopPropagation,
  debounce,
} from '../../utils';
import { ESC_KEYCODE } from '../../utils/constants/keys';
import {
  DATA_HISTORY_ENABLED,
  DATA_EXIT_ENABLED,
  DATA_ORIGIN_URL,
} from '../../utils/constants/dataKeys';

import {
  focusableSelector,
  overflowHiddenClass,
  closeModalSelector,
  modalAjaxLinkSelector,
  openModalClass,
  modalHeaderSelector,
  modalBodySelector,
  modalElevatedHeaderClass,
} from '../../utils/constants/classes';

const $window = $(window);
const $html = $(document.documentElement);

export default class Modal {
  constructor(trigger, modal) {
    if (trigger) {
      this.$trigger = $(trigger);
    }

    this.afterAnimationCallbacks = [];

    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onChangeView = this.onChangeView.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.resize = this.resize.bind(this);

    this.onAnimationEnd = this.onAnimationEnd.bind(this);
    this.onScrollEnd = debounce(this.onScrollEnd.bind(this), 300);

    if (modal) {
      this.isPreloaded = true;
      this.init(modal);
      this.open();
    }
  }

  // #region actions
  /**
   * init the modal each time it's open
   *
   * @param {(HTMLElement|Zepto)} modal
   * @returns {Modal}
   */
  init(modal) {
    this.$modal = $(modal);
    this.$container = this.$modal.children();
    this.$body = this.$container.find(modalBodySelector);
    this.$header = this.$container.find(modalHeaderSelector);

    this.$firstFocusable = this.$modal
      .find(focusableSelector)
      .first();

    this.originUrl = this.$modal.data(DATA_ORIGIN_URL);

    this.exitEnabled = this.$modal.data(DATA_EXIT_ENABLED);

    if (this.historyEnabled === undefined) {
      this.historyEnabled = this.$modal.data(DATA_HISTORY_ENABLED);
    }

    this.$container.on('click', stopPropagation);

    return this;
  }

  /**
   * add all needed event handlers, saves the last active element and focus the modal
   *
   * @param {boolean} [withAnimation=true]
   * @returns {Modal}
   */
  open(withAnimation = true) {
    if (this.isPreloaded && this.$trigger) {
      this.$lastActiveElement = this.$trigger;
    } else {
      this.$lastActiveElement = $(document.activeElement);
    }

    // Should the open modal class be added in the server too?
    $html.addClass(overflowHiddenClass);

    $window
      .off('resize', this.onResize)
      .off('keydown', this.onKeyDown)
      .off('popstate', this.onPopState)

      .on('resize', this.onResize)
      .on('keydown', this.onKeyDown)
      .on('popstate', this.onPopState);

    this.$body.on('scroll', this.onScroll);

    this.$container
      .find(modalAjaxLinkSelector)
      .on('click', this.onChangeView);

    this.$container
      .find(closeModalSelector)
      .on('click', this.onClose);

    if (this.exitEnabled) {
      this.$modal.on('click', this.onClose);
    }

    this.$modal.addClass(openModalClass);

    if (!this.isPreloaded && withAnimation) {
      this.openAnimate();
    }

    disableTabs();
    enableTabs(this.$container);
    globalInitializer(this.$container);

    this.$firstFocusable.focus();

    this.$container.find('img').on('load', this.resize);
    this.afterAnimation(this.resize);
    this.$container.on('click', hideItemsOnBodyClick);

    return this;
  }

  onAnimationEnd() {
    this.isAnimating = false;
    this.afterAnimationCallbacks.forEach((fn) => fn());
    this.afterAnimationCallbacks = [];
  }

  onScrollEnd() {
    this.isScrolling = false;
    this.toggleHeaderElevation();
  }

  /**
   * waits to execute the given function until any animation ends
   *
   * @param {Function} callback
   * @returns {Modal}
   */
  afterAnimation(callback) {
    if (typeof callback === 'function') {
      if (this.isAnimating) {
        this.afterAnimationCallbacks.push(callback);
      } else {
        callback();
      }
    }

    return this;
  }

  /**
   *
   * @returns {Modal}
   */
  openAnimate() {
    // TODO: use anime.js to animate (PDEV-36908)
    this.isAnimating = true;
    const originalBackground = this.$modal.css('background');

    this.$modal.css('background', 'transparent');
    this.$container.css('transform', 'translate(-50%, -50%) scale(0)');
    this.$modal.animate({ background: originalBackground }, 100);
    this.$container.animate({
      transform: 'translate(-50%, -50%) scale(1)',
    }, {
      complete: this.onAnimationEnd,
      duration: 200,
    });
    return this;
  }

  /**
   * changes the header's elevation according to the scroll position
   *
   * @param {Function} callback
   * @returns {Modal}
   */
  toggleHeaderElevation() {
    if (this.$body.scrollTop() === 0) {
      this.$header.removeClass(modalElevatedHeaderClass);
    } else {
      this.$header.addClass(modalElevatedHeaderClass);
    }
  }

  /**
   * resize modal's body using container's natural size
   *
   * @param {Function} callback
   * @returns {Modal}
   */
  resize() {
    // removing the static height allows the content
    // and the container expands to their natural size
    this.$body.height(null);

    // this saves the current scroll position and
    // avoid jumps while a resize is happening
    this.scrollTop = this.$body.scrollTop();
    this.$container.scrollTop(this.scrollTop);

    const totalHeight = this.$container.height();
    const headerHeight = this.$header.height();
    this.$body.height(totalHeight - headerHeight);
    this.$body.scrollTop(this.scrollTop);
    this.resizing = false;
  }

  /**
   * removes the overflow hidden class, event handler and the modal from the dom
   * and gives the focus to the last active element before the modal
   *
   * @returns {Modal}
   */
  close() {
    enableTabs();
    $html.removeClass(overflowHiddenClass);
    $window
      .off('keydown', this.onKeyDown)
      .off('resize', this.onResize);
    this.$modal.remove();

    this.isPreloaded = false;
    this.$lastActiveElement.focus();

    return this;
  }

  /**
   * changes the current html or creates the modal with the given html
   *
   * @param {string} modalHtml
   * @returns {Modal}
   */
  setHtml(modalHtml) {
    if (this.$modal) {
      this.$modal.remove();
    }

    const $element = $(modalHtml)
      .appendTo(document.body);

    return this.init($element);
  }

  /**
   * push a new state if history is enabled
   *
   * @param {object} state
   * @param {string} url
   * @param {string} [withAnalytics=true]
   * @returns {Modal}
   */
  updateHistory(state, url, withAnalytics = true) {
    const { analytics } = window;
    if (this.historyEnabled) {
      // eslint-disable-next-line no-restricted-globals
      history.pushState(state, url, url);
      if (withAnalytics && analytics && analytics.page) {
        // This is to let segment knows that the page changed
        // source: https://community.segment.com/t/q58ab2/single-page-application-spa-event-tracking
        analytics.page();
      }
    }
    return this;
  }

  /**
   * requests the html and changes the modal with it
   *
   * @param {string} href
   * @param {boolean} [obj.isBack=false] determines if the user clicked the
   *        back button
   * @param {boolean} [obj.withAnimation]
   */
  changeView(href, { isBack = false, withAnimation } = {}) {
    getHtml(href, safeGet(href, (html) => {
      this
        .setHtml(html)
        .open(withAnimation);

      globalInitializer(this.$container);

      if (!isBack) {
        this.updateHistory({
          // eslint-disable-next-line no-restricted-globals
          prevUrl: location.pathname,
        }, href);
      }
    }));

    return this;
  }
  // #endregion actions

  // #region event handlers
  onChangeView(evt) {
    const href = $(evt.target)
      .closest('a')
      .attr('href');

    this.changeView(href, { withAnimation: false });
    evt.preventDefault();
  }

  onOpen(evt) {
    const href = this.$trigger.attr('href');

    this.changeView(href);
    evt.preventDefault();
  }

  onPopState() {
    // eslint-disable-next-line no-restricted-globals
    if (history.state && history.state.prevUrl) {
      // eslint-disable-next-line no-restricted-globals
      this.changeView(location.pathname, /* isBack */ true);
    } else {
      this.close();
    }
  }

  onKeyDown(evt) {
    if (evt.keyCode === ESC_KEYCODE && this.exitEnabled) {
      this.onClose(evt);
    }
  }

  onClose(evt) {
    this.updateHistory({}, this.originUrl, /* withAnalytics */ false);
    this.close();
    evt.preventDefault();
  }

  onResize() {
    if (!this.resizing) {
      this.resizing = true;

      this.resize();
    }
  }

  onScroll() {
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.toggleHeaderElevation();
    }
    this.onScrollEnd();
  }
  // #endregion event handlers
}
